import React, {Fragment, useEffect, useState} from 'react';
import {checkPerm, ConstantTranslate, getLookup, getProp} from '../../../common/util/util-helpers'
import Resources from "../../../data/services/resources";
import {Field, FieldsManager} from "../../../data/services/fields";
import {fillFieldsFromData} from "../../../common/util/util-fields";
import {
    CREATE_PERM,
    DEFAULT_METADATA_SELECT_SEARCH_QUERY,
    PAY_METHOD_TYPES,
    UPDATE_PERM
} from "../../../util/util-constants";
import {getResource, updateResource} from "../../../data/actions/resource";
import LocalStorage from "../../../util/localStorage";
import PayAdjustmentsSection from "../employee-single/payAdjustmentsSection";
import PageFooter from "../../../common/components/layout/layout-components/page/page-footer";
import CardWithHeader from "../../../common/components/card/card-with-header";
import CardWithHeaderSubTitle from "../../../common/components/card/card-with-header-sub-title";
import {LoaderLarge} from "../../../common/components/loader";
import {getThirdResource} from "../../../data/actions/thirdResource";
import FieldsForm from "../../../common/components/fields/fields-form";
import FieldGroupConditional from "../../../common/components/fields/field-group-conditional";

const AgentPayrollTab = ({translate, dispatch, id, resource, secondResource, setIsStateDirty, thirdResource}) => {

    /** Helpers
     ================================================================= */
    const data = getProp(resource, 'data', {})
    const isLoading = getProp(resource, 'isLoading', false)
    const isThirdLoading = getProp(thirdResource, 'isLoading', false)
    const ContactID = getProp(thirdResource, 'data.ContactID','')

    const getResourceName = () => {
        return Resources.AgentsSettlements
    }

    const getQuery = () => {
        return {
            id: id
        }
    }

    const getPayMethodValueNote = (type) => {
        if (type == 1) return 'Amount paid each month';
        if (type == 2) return 'Amount paid each week';
        if (type == 3) return 'Rate per hour worked';
        return '';
    };

    const getFields = (item = null) => {
        let fieldTemplates = {
            PayMethodType: new Field('PayMethodType', '', [''], false, 'select', {
                addContainerClass: 'col-span-6'
            }, {values: ConstantTranslate(PAY_METHOD_TYPES, translate)}),
            PayMethodValue: new Field('PayMethodValue', '', [''], false, 'money', {addContainerClass: 'col-span-6', note: item ? getPayMethodValueNote(item.PayMethodType) : ''}),
            Send1099: new Field('Send1099', '', [], false, 'checkbox-card', {
                addContainerClass: 'col-span-full',
                labelRaw: "Issue 1099 tax form",
                note: "Send a 1099 to this agent at year-end. Use this for contractors paid as agents — not for W-2 staff.",
                labelType: "float",
            }),
            DepositAccountID: new Field('DepositAccountID', '', [], false, 'select-search', {
                addContainerClass: 'col-span-full',
                labelRaw: "Chart of accounts",
                note: "The GL account this agent's payroll expenses post to.",
            }, {
                api: 'api/' + Resources.AccountsQuick,
                query: {},
                searchMap: (item) => ({
                    value: item.AccountID,
                    label: item.AccountNumber + ' ' + item.AccountName
                })
            }),
            DepositProvider: new Field('DepositProvider', '', [], false, 'text', {
                addContainerClass: 'col-span-full',
                labelRaw: "Payroll bill label (optional)",
                note: "A custom tag applied to each payroll bill — makes filtering and reporting easier.",
                placeholder: "e.g. Office Staff, Part-time"
            }),
            IsFactoringActive: new Field('IsFactoringActive', '', [], false, 'switch', {
                addContainerClass: "-mx-4 pr-4 col-span-full flex items-center hover:bg-sky-600/10 rounded-xl",
                label: 'Pay a different organization',
                note: "Route this agent's pay to a third party — such as a professional employer organization (PEO), staffing agency, or employer of record."
            }),
            OrganizationID: new Field('OrganizationID', '', [], false, 'select-search', {addContainerClass: 'col-span-full z-30', labelRaw: "Organization name"})
        }
        if (item) {
            fieldTemplates.OrganizationID.value = !item?.IsFactoringActive ? "" : item.OrganizationID;
            fieldTemplates.OrganizationID.disabled = !item?.IsFactoringActive;
        }

        return fillFieldsFromData(fieldTemplates, item)
    }

    /** State
     ================================================================= */
    const [fields, setFields] = useState(() => getFields())
    const [canSubmit, setCanSubmit] = useState(false)

    /** Lifecycle
     ================================================================= */
    useEffect(() => {
        dispatch(getResource({
            user: LocalStorage.get("user"),
            resource: getResourceName(),
            query: getQuery()
        }));

        dispatch(getThirdResource({
            user: LocalStorage.get('user'),
            resource: Resources.AgentInfo,
            query: getQuery()
        }))
    }, [])

    useEffect(() => {
        if (!isLoading && !!Object.keys(data).length) {
            setFields(getFields(data))
        }
    }, [isLoading])

    useEffect(() => {
        setIsStateDirty(canSubmit)
    }, [canSubmit])

    /** UI Events
     ================================================================= */
    const handleInputChange = (name, value) => {
        let fieldsTmp = FieldsManager.updateField(fields, name, value);

        if (name === 'IsFactoringActive') {
            fieldsTmp.OrganizationID.value = !value ? "" : fieldsTmp.OrganizationID.value;
            fieldsTmp.OrganizationID.disabled = !value;
        }

        if (name === 'PayMethodType') {
            fieldsTmp.PayMethodValue.metadata.note = getPayMethodValueNote(value);
        }

        setFields(fieldsTmp)
        setCanSubmit(true)
    }

    const handleFormCancel = () => {
        setFields(getFields(data))
        setCanSubmit(false)
    }

    const onSubmit = () => {
        const fieldsTmp = FieldsManager.validateFields(fields)
        if (FieldsManager.checkFieldsForErrors(fieldsTmp)) {
            dispatch(updateResource({
                user: LocalStorage.get('user'),
                params: Object.assign({}, FieldsManager.getFieldKeyValues(fields), {
                    AgentSettlementID: getProp(resource, 'data.AgentSettlementID', 0)
                }),
                resource: getResourceName(),
                piggyResource: getResourceName(),
                piggyQuery: getQuery(),
                errorMessage: true, successMessage: 'Agent settlement successfully updated!',
            }))
        } else {
            setFields(fieldsTmp)
        }
        setCanSubmit(false)
    }

    /** Render
     ================================================================= */
    const selects = {
        OrganizationID: {
            api: 'api/' + Resources.OrganizationsQuick,
            query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
            searchMap: (item) => {
                return ({
                    value: item.OrganizationID,
                    label: item.LegalName + " City: " + item.CityName + " State: " + getLookup('State')[item.StateID] + " Zip: " + item.PostalCode,
                    metadata: item
                })
            }
        },
        DepositAccountID: {
            api: 'api/' + Resources.AccountsQuick,
            query: {},
            searchMap: (it) => {
                return {
                    label: it.AccountNumber + ' ' + it.AccountName,
                    value: it.AccountID
                }
            }
        }
    }

    return (
        <Fragment>
            <div className="grid grid-cols-9 gap-4">
                <div className="col-span-full 3xl:col-span-3">
                    <CardWithHeader
                        title={translate('text.Payroll')}
                        description="How this agent is compensated each pay cycle"
                    >
                        {isLoading && <LoaderLarge stripesBg/>}

                        <CardWithHeaderSubTitle subTitle="Base Compensation"/>

                        <div className="grid grid-cols-12 gap-4 mb-6">
                            <FieldsForm
                                fieldsState={fields}
                                onChange={handleInputChange}
                                selects={selects}
                                includeFields={['PayMethodType', 'PayMethodValue', 'Send1099']}
                                translate={translate}
                            />
                        </div>

                        <CardWithHeaderSubTitle subTitle="Payment routing"/>

                        <div className="grid grid-cols-12 gap-4 mb-6">
                            <FieldsForm
                                fieldsState={fields}
                                onChange={handleInputChange}
                                selects={selects}
                                includeFields={['DepositAccountID', 'DepositProvider']}
                                translate={translate}
                            />
                        </div>

                        <CardWithHeaderSubTitle subTitle="Pay to override"/>

                        <FieldGroupConditional
                            addClass={"bg-tm-gray-50"}
                            switchFieldName={'IsFactoringActive'}
                            fieldsState={fields}
                            toggleFields={['OrganizationID']}
                            onChange={handleInputChange}
                            selects={{
                                OrganizationID: {
                                    api: 'api/' + Resources.OrganizationsQuick,
                                    query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
                                    searchMap: (item) => {
                                        return ({
                                            value: item.OrganizationID,
                                            label: item.LegalName + " City: " + item.CityName + " State: " + getLookup('State')[item.StateID] + " Zip: " + item.PostalCode,
                                            metadata: item
                                        })
                                    }
                                }
                            }}
                            translate={translate}
                        />
                    </CardWithHeader>
                </div>
                {!isThirdLoading && (
                    <div className="col-span-full 3xl:col-span-6">
                        <PayAdjustmentsSection
                            translate={translate}
                            secondResource={secondResource}
                            resourceName={Resources.AgentsDeduction}
                            ContactID={ContactID}
                            dispatch={dispatch}
                        />
                    </div>
                )}

            </div>
            <PageFooter
                translate={translate}
                canSubmit={canSubmit}
                actionCancel={handleFormCancel}
                actionSubmit={checkPerm(getResourceName(), UPDATE_PERM) && onSubmit}
            />

        </Fragment>
    )
}
export default AgentPayrollTab;